import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, 
  Info, 
  Check, 
  Crown, 
  Wrench, 
  Dog, 
  Stethoscope, 
  Layers, 
  Store, 
  Factory, 
  Scissors, 
  ShieldCheck, 
  Building2 
} from 'lucide-react';
import { safeStorage } from '../../utils/safeStorage';

export default function PricingSection({ 
  onOpenModal,
  onSelectPlan 
}: { 
  onOpenModal?: () => void;
  onSelectPlan?: (planId: 'monthly' | 'annual' | 'specialist' | 'specialist_annual' | 'enterprise' | 'enterprise_annual' | 'empresa' | 'empresa_anual') => void;
}) {
  const [isAnnual, setIsAnnual] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState('premium');
  const navigate = useNavigate();

  const iniciarFluxo = (plano: string, _mpUrl?: string) => {
    const normalizedPlan = (plano === 'premium' || plano === 'specialist') 
      ? 'especialista' 
      : (plano === 'enterprise' ? 'empresa' : plano);
    safeStorage.setItem('rmax_pending_plan', normalizedPlan);

    let valor = 0;
    if (plano === 'pro') {
      valor = isAnnual ? 195.00 : 19.90;
    } else if (plano === 'premium') {
      valor = isAnnual ? 499.00 : 49.90;
    } else if (plano === 'enterprise' || plano === 'empresa') {
      valor = isAnnual ? 990.00 : 99.00;
    }

    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'clique_plano', {
        'plano_escolhido': plano + (plano === 'pro' || plano === 'premium' || plano === 'enterprise' || plano === 'empresa' ? (isAnnual ? '_anual' : '_mensal') : ''),
        'valor': valor
      });
    }

    if (plano === 'gratis') {
      if (onSelectPlan) {
        onSelectPlan('monthly');
      } else {
        navigate('/selecionar-perfil?tab=pro_gratis&plan=gratis');
      }
      return;
    }

    if (plano === 'pro') {
      const planKey = isAnnual ? 'annual' : 'monthly';
      if (onSelectPlan) {
        onSelectPlan(planKey);
      } else {
        navigate('/selecionar-perfil?tab=pro_gratis&plan=pro');
      }
      return;
    }

    if (plano === 'premium') {
      const planKey = isAnnual ? 'specialist_annual' : 'specialist';
      if (onSelectPlan) {
        onSelectPlan(planKey);
      } else {
        navigate('/selecionar-perfil?tab=especialista&plan=especialista');
      }
      return;
    }

    if (plano === 'enterprise' || plano === 'empresa') {
      const planKey = isAnnual ? 'enterprise_annual' : 'enterprise';
      if (onSelectPlan) {
        onSelectPlan(planKey);
      } else {
        navigate('/selecionar-perfil?tab=empresa&plan=empresa');
      }
      return;
    }
  };

  interface PricingPlan {
    id: string;
    name: string;
    description: string;
    price: string;
    subtext?: string;
    billingInfo?: string;
    features: (string | { label: string; text: string })[];
    buttonText: string;
    featured: boolean;
    disabled?: boolean;
    badge?: string;
  }

  const plans: PricingPlan[] = [
    {
      id: 'gratis',
      name: 'Plano Grátis',
      description: 'Para quem está começando e precisa de orçamentos precisos sem custo.',
      price: 'R$ 0,00',
      features: [
        { label: '5 Orçamentos/mês:', text: 'Crie por áudio, foto e texto ou no Modo Direto.' },
        { label: 'Valor da sua Hora:', text: 'Descubra quanto vale a sua hora de trabalho real.' },
        { label: 'Termômetro de Lucro:', text: 'Preços calculados para nunca mais ter prejuízo.' },
        'Grátis para sempre, sem cartão de crédito'
      ],
      buttonText: 'Usar Sempre Grátis',
      featured: false
    },
    {
      id: 'pro',
      name: 'Assinatura Pro',
      description: 'Velocidade, volume e margem de lucro garantida para o seu negócio.',
      price: isAnnual ? '10x R$ 19,50' : 'R$ 19,90',
      subtext: isAnnual ? ' sem juros' : '/mês',
      billingInfo: isAnnual 
        ? 'Ou R$ 195,00 à vista (Economia de R$ 43,80/ano)' 
        : 'Acesso total e ilimitado à plataforma',
      features: [
        { label: 'Orçamentos Ilimitados:', text: 'Gere propostas profissionais em PDF ou no Modo Direto sem limites.' },
        { label: 'Custos & Materiais:', text: 'Cadastro de Insumos, Serviços, Fichas Técnicas e Kits.' },
        { label: 'Histórico & PDF Profissional:', text: 'Reutilize propostas e envie PDFs personalizados.' },
        { label: 'Controle Financeiro Real:', text: 'Custos fixos, pró-labore e margem calculados na hora.' }
      ],
      buttonText: isAnnual ? 'Garantir Plano Anual' : 'Assinar Plano Mensal',
      featured: false
    },
    {
      id: 'premium',
      name: 'Especialista Premium',
      description: 'Gestão completa para equipes de até 10 pessoas, blindagem de margem e controle total da sua empresa.',
      price: isAnnual ? '10x R$ 49,90' : 'R$ 49,90',
      subtext: isAnnual ? ' sem juros' : '/mês',
      billingInfo: isAnnual 
        ? 'Ou R$ 499,00 à vista (Economia de R$ 99,80/ano)' 
        : 'Acesso completo a todas as ferramentas sem fidelidade',
      features: [
        { label: 'Minha Equipe (Até 10 Colaboradores):', text: 'Convide sua equipe sem custo extra. Cada um acessa no celular ou PC.' },
        { label: 'Trava de Segredo Comercial:', text: 'Vendedores e técnicos emitem orçamentos sem ver seus custos reais nem sua margem.' },
        { label: 'Orçamentos & PDFs Ilimitados:', text: 'Toda a sua empresa gerando propostas comerciais sem pegadinhas.' },
        { label: 'Tudo do Plano PRO:', text: 'Orçamentos (IA + Direto), Insumos, Serviços, Fichas Técnicas e Kits.' },
        { label: 'Ordens de Serviço com Fotos:', text: 'Vistoria do Antes e Depois com até 12 fotos para blindar seu serviço.' },
        { label: 'Assinatura Touch & Recibos PIX:', text: 'Aceite do cliente na tela do celular e recibos instantâneos.' },
        { label: 'Agenda & Link Online:', text: 'Grade de horários da equipe e link direto de agendamento.' },
        { label: 'Clientes & Preços da Região:', text: 'Histórico, rotas no mapa e comparação de valores regionais.' }
      ],
      buttonText: isAnnual ? 'Garantir Premium Anual' : 'Assinar Especialista Mensal',
      featured: true
    },
    {
      id: 'enterprise',
      name: 'Plano Empresa',
      description: 'Empresas, Balcão PDV, Indústria & Clínicas',
      price: isAnnual ? '10x R$ 99,00' : 'R$ 99,00',
      subtext: isAnnual ? ' sem juros' : '/mês',
      billingInfo: isAnnual 
        ? 'Ou R$ 990,00 à vista (Economia de R$ 198,00/ano)' 
        : 'Acesso completo sem fidelidade',
      features: [
        { label: 'Tríade Fiscal Direta SEFAZ:', text: 'Emissão de NFC-e, NF-e e NFS-e via certificado digital A1 sem taxas por nota emitida.' },
        { label: 'Frente de Caixa PDV & Balança:', text: 'Venda de balcão ágil com tara e leitura serial P03 (Toledo, Filizola, Urano) e modo offline.' },
        { label: 'PCP Indústria & Fichas Técnicas:', text: 'Estrutura de produto (BOM), ordens de produção e etiquetas térmicas ZPL/RFID.' },
        { label: 'Estoque com Lotes & Validade:', text: 'Ponto de pedido, alerta de vencimento e baixa automática por PEPS (100% sem gráficos).' },
        { label: 'Prontuário Médico SOAP & Clínicas:', text: 'Prontuário eletrônico, suporte multi-pet, central de retorno e cofre de laudos/DICOM em nuvem R2.' },
        { label: 'Portal do Contador & Reforma 2026:', text: 'Relatórios contábeis em CSV/Excel e preparação nativa para CBS/IBS/Split Payment.' },
        { label: 'Tudo do Especialista Premium:', text: 'Orçamentos ilimitados, fotos de OS, assinatura digital e recibos PIX.' }
      ],
      buttonText: isAnnual ? 'Garantir Plano Empresa Anual' : 'Assinar Plano Empresa Mensal',
      featured: false,
      badge: 'EMPRESAS, INDÚSTRIA & PDV'
    }
  ];

  return (
    <section className="container mx-auto px-6 py-24 md:py-32 relative scroll-mt-20 overflow-x-hidden" id="precos" aria-labelledby="pricing-heading">
      <div className="text-center mb-12 md:mb-16 relative z-10">
        <h2 id="pricing-heading" className="text-3xl md:text-5xl font-extrabold text-slate-950 mb-4 tracking-tight px-4">Comece de graça, escale com inteligência</h2>
        <p className="text-emerald-700 text-base md:text-lg font-bold px-4">Planos desenhados para você nunca mais chutar um preço na vida.</p>
        
        {/* Seletor de Faturamento Mensal/Anual com Área de Toque Adaptada (mínimo 48px) */}
        <div className="flex justify-center items-center gap-4 mt-8 relative z-10">
          <span className={`text-base transition-colors duration-300 ${!isAnnual ? 'text-slate-950 font-bold' : 'text-slate-500'}`}>Mensal</span>
          <button 
            type="button"
            onClick={() => setIsAnnual(!isAnnual)}
            className="w-16 h-9 min-h-12 bg-slate-200 border border-slate-300 rounded-full p-1 relative flex items-center transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
            aria-label="Alternar faturamento mensal ou anual"
          >
            <div className={`w-6 h-6 bg-emerald-600 rounded-full shadow-sm transform transition-transform duration-300 ${isAnnual ? 'translate-x-7' : 'translate-x-0'}`} />
          </button>
          <span className={`text-base flex items-center gap-2 transition-colors duration-300 ${isAnnual ? 'text-slate-950 font-bold' : 'text-slate-500'}`}>
            Anual
            <span className="bg-emerald-100 text-emerald-800 text-xs font-black px-2.5 py-1 rounded-full border border-emerald-300">
              Economize até 18%
            </span>
          </span>
        </div>
      </div>

      {/* Navegador Rápido de Nichos Verticais */}
      <div className="max-w-5xl mx-auto mb-12 px-4 relative z-10">
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Soluções Especializadas por Segmento:
            </span>
            <span className="text-[11px] text-slate-500 font-medium">
              Conheça as páginas dedicadas a cada fluxo de trabalho
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            <button
              type="button"
              onClick={() => navigate('/assistencia')}
              className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-white border border-slate-200 hover:border-emerald-600 hover:text-emerald-700 text-xs font-semibold text-slate-700 transition-all cursor-pointer shadow-2xs"
            >
              <Wrench className="w-3.5 h-3.5 shrink-0" />
              <span>Assis. Técnica</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/vet')}
              className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-white border border-slate-200 hover:border-emerald-600 hover:text-emerald-700 text-xs font-semibold text-slate-700 transition-all cursor-pointer shadow-2xs"
            >
              <Dog className="w-3.5 h-3.5 shrink-0" />
              <span>Veterinária</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/med')}
              className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-white border border-slate-200 hover:border-emerald-600 hover:text-emerald-700 text-xs font-semibold text-slate-700 transition-all cursor-pointer shadow-2xs"
            >
              <Stethoscope className="w-3.5 h-3.5 shrink-0" />
              <span>Saúde & Méd</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/servicos')}
              className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-white border border-slate-200 hover:border-emerald-600 hover:text-emerald-700 text-xs font-semibold text-slate-700 transition-all cursor-pointer shadow-2xs"
            >
              <Layers className="w-3.5 h-3.5 shrink-0" />
              <span>Serviços</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/selecionar-perfil?tab=empresa&niche=food_icecream')}
              className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-white border border-slate-200 hover:border-emerald-600 hover:text-emerald-700 text-xs font-semibold text-slate-700 transition-all cursor-pointer shadow-2xs"
            >
              <Store className="w-3.5 h-3.5 shrink-0" />
              <span>Sorveteria</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/selecionar-perfil?tab=empresa&niche=industrial_pcp')}
              className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-white border border-slate-200 hover:border-emerald-600 hover:text-emerald-700 text-xs font-semibold text-slate-700 transition-all cursor-pointer shadow-2xs"
            >
              <Factory className="w-3.5 h-3.5 shrink-0" />
              <span>Indústria</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/beleza')}
              className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-white border border-slate-200 hover:border-emerald-600 hover:text-emerald-700 text-xs font-semibold text-slate-700 transition-all cursor-pointer shadow-2xs"
            >
              <Scissors className="w-3.5 h-3.5 shrink-0" />
              <span>Beleza</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto relative z-10 items-stretch">
        {plans.map((plan) => {
          const isSelected = selectedPlan === plan.id;
          return (
            <div 
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`rounded-3xl p-6 sm:p-7 transition-all duration-300 relative flex flex-col justify-between cursor-pointer border ${
                plan.featured 
                  ? 'bg-white border-2 border-emerald-600 shadow-xl shadow-emerald-950/5 ring-2 ring-emerald-500/20 md:-translate-y-2' 
                  : plan.id === 'enterprise'
                    ? 'bg-white border-2 border-blue-300 hover:border-blue-400 shadow-md shadow-blue-950/5'
                    : 'bg-white border border-slate-200 hover:border-slate-300 shadow-sm'
              } ${isSelected && !plan.featured && plan.id !== 'enterprise' ? 'ring-2 ring-emerald-600 border-emerald-600' : ''}`}
            >
              {plan.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs font-black px-4 py-1.5 rounded-full uppercase tracking-wider shadow-md flex items-center gap-1.5 whitespace-nowrap">
                  <Crown className="w-3.5 h-3.5" aria-hidden="true" />
                  Mais Escolhido
                </div>
              )}

              {plan.badge && !plan.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-linear-to-r from-blue-600 to-indigo-600 text-white text-xs font-black px-4 py-1.5 rounded-full uppercase tracking-wider shadow-md flex items-center gap-1.5 whitespace-nowrap">
                  <Crown className="w-3.5 h-3.5" aria-hidden="true" />
                  {plan.badge}
                </div>
              )}

              <div className="flex-1 flex flex-col">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                  <p className="text-slate-600 text-sm min-h-10 leading-relaxed">{plan.description}</p>
                </div>

                <ul className="space-y-3.5 mb-6 text-sm" aria-label={`Recursos do plano ${plan.name}`}>
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-slate-700">
                      <Check className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="leading-snug">
                        {typeof feature === 'string' ? (
                          feature
                        ) : (
                          <>
                            <strong className="text-slate-900 font-semibold">{feature.label}</strong> {feature.text}
                          </>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>

                {plan.id === 'pro' && (
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 bg-slate-50 p-3 rounded-2xl border border-slate-200 mb-5 relative z-10 text-left mt-auto">
                    <Info className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
                    <p className="leading-relaxed">
                      Acesso imediato e ilimitado para alavancar os lucros da sua empresa sem travas.
                    </p>
                  </div>
                )}

                {plan.id === 'premium' && (
                  <div className="flex items-start gap-2.5 text-xs text-amber-950 bg-amber-50 p-3 rounded-2xl border border-amber-200 mb-5 relative z-10 text-left mt-auto">
                    <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
                    <p className="leading-relaxed">
                      <strong>Gestão e Organização Completa:</strong> Assinatura no celular, Agenda, Recibos com PIX, Cadastro de Clientes e OS com Fotos.
                    </p>
                  </div>
                )}

                {plan.id === 'enterprise' && (
                  <div className="flex items-start gap-2.5 text-xs text-blue-950 bg-blue-50 p-3 rounded-2xl border border-blue-200 mb-5 relative z-10 text-left mt-auto">
                    <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" aria-hidden="true" />
                    <p className="leading-relaxed">
                      <strong>Plano Empresa:</strong> Tríade Fiscal Direta SEFAZ, Frente de Caixa PDV com Balança, PCP Indústria, Lotes/Validade e Prontuário Clínico.
                    </p>
                  </div>
                )}
              </div>

              {/* Bloco de Preço no Rodapé + Botão CTA */}
              <div className="mt-auto pt-5 border-t border-slate-100">
                <div className="mb-4">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-2xl sm:text-3xl font-extrabold text-slate-950">{plan.price}</span>
                    {plan.subtext && <span className="text-slate-500 text-sm font-semibold">{plan.subtext}</span>}
                  </div>
                  {plan.billingInfo && (
                    <p className="text-xs text-emerald-700 mt-1.5 font-medium leading-relaxed">
                      {plan.billingInfo}
                    </p>
                  )}
                </div>

                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    iniciarFluxo(plan.id);
                  }}
                  disabled={plan.disabled}
                  aria-label={`Escolher plano ${plan.name}`}
                  className={`w-full min-h-12 py-3.5 px-6 rounded-full font-bold text-sm sm:text-base transition-all duration-300 transform active:scale-95 outline-none relative overflow-hidden flex items-center justify-center cursor-pointer ${
                    isSelected || plan.featured
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-md shadow-emerald-600/20' 
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200'
                  } ${plan.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  <span className="relative z-10">{plan.buttonText}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

